import React, {useEffect, useMemo, useState} from 'react';
import {useFundraiserDocuments} from '../../..';
import {FundraiserDocumentsTable} from '../../../../widgets/FundraiserDocumentsTable';
import {ErrorModal, useErrorHandler, DocumentViewer} from '../../../../shared';
import {DeleteFundraiserDocumentModal} from '../../../../features';
import {FundraiserDocumentModel} from '../../model';

interface FundraiserDocumentsProps {
  appealId: string;
}

export const FundraiserDocuments = ({appealId}: FundraiserDocumentsProps) => {
  const {getInfiniteDocuments} = useFundraiserDocuments(appealId);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentIdToDelete, setDocumentIdToDelete] = useState<string>('');
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [documentToView, setDocumentToView] = useState<FundraiserDocumentModel | null>(null);

  const {
    data: infiniteDocumentsData,
    error: documentsError,
    isLoading: isDocumentsLoading,
    fetchNextPage: fetchNextDocumentsPage,
    hasNextPage: hasNextDocumentsPage,
    isFetchingNextPage: isFetchingNextDocumentsPage,
  } = getInfiniteDocuments;

  const {handleError, isErrorModalOpen, errorInfo, handleClose} = useErrorHandler({
    resourceName: 'Документы',
    title: 'Ошибка при получении документов',
  });

  const documents = useMemo(
    () => infiniteDocumentsData?.pages.flatMap((page: any) => page.items) || [],
    [infiniteDocumentsData],
  );

  useEffect(() => {
    if (documentsError) {
      handleError(documentsError as unknown as ApiError);
    }
  }, [documentsError, handleError]);

  const handleDeleteDocument = (documentId: string) => {
    setDocumentIdToDelete(documentId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDocumentIdToDelete('');
  };

  const handleViewDocument = (document: FundraiserDocumentModel) => {
    setDocumentToView(document);
    setIsViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setDocumentToView(null);
  };

  return (
    <>
      <FundraiserDocumentsTable
        documents={documents}
        isLoading={isDocumentsLoading}
        fetchNextPage={fetchNextDocumentsPage}
        hasNextPage={hasNextDocumentsPage}
        isFetchingNextPage={isFetchingNextDocumentsPage}
        onDeleteDocument={handleDeleteDocument}
        onViewDocument={handleViewDocument}
      />
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
      <DeleteFundraiserDocumentModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        appealId={appealId}
        documentId={documentIdToDelete}
      />
      {documentToView && (
        <DocumentViewer
          open={isViewerOpen}
          onClose={handleCloseViewer}
          documentUrl={documentToView.url}
          documentTitle={documentToView.title}
          fileName={documentToView.fileName}
        />
      )}
    </>
  );
};
